import { TicketPriority } from '@21k/shared';
import { FormEvent, useMemo, useState } from 'react';

import {
  getApiErrorMessage,
  listAssignableMembers,
  listTicketCategoryOptions,
  listTicketLabelOptions,
  updateTicketCategory,
  updateTicketLabels,
  updateTicketPriority,
  updateTicketStatus,
} from '../../api/client';
import type {
  AssignableTeamGroup,
  TicketCategoryRef,
  TicketDetail,
  TicketLabelRef,
  TicketStatusRef,
  TicketStatusWorkflow,
} from '../../api/types';
import TicketLabelPicker from '../../components/TicketLabelPicker';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { changeTicketAssigneeWithWarning } from '../../utils/ticketAssignee';
import { formatStatus, resolveAllowedStatusIds } from '../../utils/ticket';

type Props = {
  ticket: TicketDetail;
  workflow: TicketStatusWorkflow;
  labelOptions: TicketLabelRef[];
  categoryOptions: TicketCategoryRef[];
  assignableTeams: AssignableTeamGroup[];
  onUpdated: () => void;
  onError: (message: string) => void;
};

export default function TicketEditPanel({
  ticket,
  workflow,
  labelOptions,
  categoryOptions,
  assignableTeams,
  onUpdated,
  onError,
}: Props) {
  const [statusComment, setStatusComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const {
    confirm,
    confirmOpen,
    confirmMessage,
    handleConfirm,
    handleCancel,
  } = useConfirmDialog();

  const allowedStatuses = useMemo(() => {
    const ids = resolveAllowedStatusIds(ticket.statusId, workflow);
    return workflow.statuses.filter((s) => ids.includes(s.id));
  }, [ticket.statusId, workflow]);

  const assigneeOptions = useMemo(() => {
    const seen = new Set<string>();
    const rows: { id: string; label: string }[] = [];
    for (const team of assignableTeams) {
      for (const member of team.members) {
        if (seen.has(member.userId)) continue;
        seen.add(member.userId);
        rows.push({
          id: member.userId,
          label: `${member.firstName} ${member.lastName} (${team.teamName})`,
        });
      }
    }
    return rows;
  }, [assignableTeams]);

  async function handleAssigneeChange(assignedToId: string | null) {
    setSaving(true);
    onError('');
    try {
      const result = await changeTicketAssigneeWithWarning({
        ticketId: ticket.id,
        assignedToId,
        previousAssignedToId: ticket.assignedToId,
        assignableTeams,
        confirm,
      });
      if (result.applied) {
        onUpdated();
      }
    } catch (err) {
      onError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function runAction(fn: () => Promise<unknown>) {
    setSaving(true);
    onError('');
    try {
      await fn();
      onUpdated();
    } catch (err) {
      onError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleStatusSubmit(e: FormEvent) {
    e.preventDefault();
    if (!newStatus || !statusComment.trim()) return;
    await runAction(() => updateTicketStatus(ticket.id, newStatus, statusComment.trim()));
    setNewStatus('');
    setStatusComment('');
  }

  return (
    <div className="card card-body sidebar-card ticket-edit-panel">
      <h3>Edit ticket</h3>

      <div className="form-group">
        <label>Category</label>
        <select
          className="form-control"
          value={ticket.categoryId}
          disabled={saving}
          onChange={(e) =>
            void runAction(() => updateTicketCategory(ticket.id, e.target.value))
          }
        >
          {categoryOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Labels</label>
        <TicketLabelPicker
          options={labelOptions}
          selectedIds={ticket.labels.map((l) => l.id)}
          onChange={(nextIds) => void runAction(() => updateTicketLabels(ticket.id, nextIds))}
        />
      </div>

      <div className="form-group">
        <label>Priority</label>
        <select
          className="form-control"
          value={ticket.priority}
          disabled={saving}
          onChange={(e) =>
            void runAction(() =>
              updateTicketPriority(ticket.id, e.target.value as TicketPriority),
            )
          }
        >
          {Object.values(TicketPriority).map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Assign to</label>
        <select
          className="form-control"
          value={ticket.assignedToId ?? ''}
          disabled={saving}
          onChange={(e) => void handleAssigneeChange(e.target.value || null)}
        >
          <option value="">Unassigned</option>
          {assigneeOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.label}
            </option>
          ))}
        </select>
      </div>

      {!ticket.status.isTerminal && allowedStatuses.length > 0 && (
        <form onSubmit={(e) => void handleStatusSubmit(e)} className="ticket-status-form">
          <h4>Change status</h4>
          <div className="form-group">
            <select
              className="form-control"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              required
              disabled={saving}
            >
              <option value="">Select status…</option>
              {allowedStatuses.map((s: TicketStatusRef) => (
                <option key={s.id} value={s.id}>
                  {formatStatus(s)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <textarea
              className="form-control"
              rows={2}
              placeholder="Comment required for status change"
              value={statusComment}
              onChange={(e) => setStatusComment(e.target.value)}
              required
              disabled={saving}
            />
          </div>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
            {saving ? 'Saving…' : 'Update status'}
          </button>
        </form>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm assignment"
        message={confirmMessage}
        confirmLabel="Proceed"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}

/** Preload options used by the edit panel */
export async function loadTicketEditOptions(departmentId: string) {
  const [labelOptions, categoryOptions, assignableTeams] = await Promise.all([
    listTicketLabelOptions(),
    listTicketCategoryOptions(),
    listAssignableMembers(departmentId),
  ]);
  return { labelOptions, categoryOptions, assignableTeams };
}
